import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Container,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Paper,
    useTheme,
    useMediaQuery,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    LinearProgress,
    IconButton,
    Dialog,
    DialogContent,
    Fab,
    Tooltip
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Gor } from '../types';
import { API_ENDPOINTS } from '../config/api';
// Import API config to set axios defaults
import '../config/api';
import { renderAddressWithMapLink } from '../utils/addressUtils';
import { calculateBookingPrice } from '../utils/bookingPricing';
import SortIcon from '@mui/icons-material/Sort';
import Divider from '@mui/material/Divider';
import { AccessTime, CalendarMonth, ChevronLeft, ChevronRight, Place } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

function getHariFromTanggal(tanggal: string) {
  if (!tanggal) return '';
  const [tgl, bulan, tahun] = tanggal.split(' ');
  const bulanMap: { [key: string]: number } = {
    'Januari': 0, 'Februari': 1, 'Maret': 2, 'April': 3, 'Mei': 4, 'Juni': 5,
    'Juli': 6, 'Agustus': 7, 'September': 8, 'Oktober': 9, 'November': 10, 'Desember': 11
  };
  try {
    const bulanIdx = bulanMap[bulan as keyof typeof bulanMap] ?? -1;
    if (bulanIdx === -1) return '';
    const date = new Date(parseInt(tahun), bulanIdx, parseInt(tgl));
    return date.toLocaleDateString('id-ID', { weekday: 'long' });
  } catch {
    return '';
  }
}

interface Banner {
  imageUrl: string;
  link?: string | null;
}

const Home: React.FC = () => {
    const navigate = useNavigate();
    const [gorList, setGorList] = useState<Gor[]>([]);
    const [coachingList, setCoachingList] = useState<Gor[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [sortBy, setSortBy] = useState<'waktu' | 'nama' | 'biaya' | 'kapasitas' | 'pesertaTerbanyak' | 'pesertaTersedikit'>('waktu');
    const isDark = theme.palette.mode === 'dark';
    const [searchTerm, setSearchTerm] = useState('');
    const [banners, setBanners] = useState<Banner[]>([]);
    const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
    const [openPoster, setOpenPoster] = useState(false);
    const [posters, setPosters] = useState<{ id: number; imageUrl: string; link: string | null }[]>([]);
    const [currentPosterIndex, setCurrentPosterIndex] = useState(0);
    const rankedRef = React.useRef<HTMLDivElement>(null);
    const funRef = React.useRef<HTMLDivElement>(null);
    const coachingRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchHomePageData = async () => {
            try {
                const response = await axios.get(API_ENDPOINTS.HOME_PAGE_DATA);
                setBanners(response.data.banners || []);
                setPosters(response.data.posters || []);
                setGorList(response.data.gorList || []);
            } catch (error) {
                console.error('Error fetching home page data:', error);
                setError('Gagal memuat data');
            } finally {
                setLoading(false);
            }
        };

        const fetchCoachingList = async () => {
            try {
                const response = await axios.get<Gor[]>('/api/coaching?action=coaching-list-with-bookings');
                const normalized = response.data.map((item) => ({
                    ...item,
                    tipe: 'Coaching',
                }));
                setCoachingList(normalized);
            } catch (error) {
                console.error('Error fetching coaching data:', error);
                setCoachingList([]);
            }
        };

        fetchHomePageData();
        fetchCoachingList();
    }, []);

    // Auto slide banner
    useEffect(() => {
        if (banners.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentBannerIndex((prevIndex) => 
                prevIndex === banners.length - 1 ? 0 : prevIndex + 1
            );
        }, 5000); // Auto slide setiap 5 detik

        return () => clearInterval(interval);
    }, [banners.length]);

    useEffect(() => {
        // Show popup if not closed in this session and posters are available
        if (posters.length > 0) {
            // Cek apakah semua poster sudah ditutup
            const closedPosters = sessionStorage.getItem('raketersPosterClosed');
            const closedIndices = closedPosters ? JSON.parse(closedPosters) : [];
            
            // Cari poster pertama yang belum ditutup
            const nextPosterIndex = posters.findIndex((_, index) => !closedIndices.includes(index));
            
            if (nextPosterIndex !== -1) {
                setCurrentPosterIndex(nextPosterIndex);
                setOpenPoster(true);
            }
        }
    }, [posters]);

    const handleBannerPrevious = () => {
        setCurrentBannerIndex(currentBannerIndex === 0 ? banners.length - 1 : currentBannerIndex - 1);
    };

    const handleBannerNext = () => {
        setCurrentBannerIndex(currentBannerIndex === banners.length - 1 ? 0 : currentBannerIndex + 1);
    };

    const handleBannerDotClick = (index: number) => {
        setCurrentBannerIndex(index);
    };

    const handleGorClick = (gor: Gor) => {
        const isCoaching = gor.tipe?.toLowerCase().includes('coaching');
        if (isCoaching) {
            navigate(`/coaching/${gor.id}`, { state: { gor } });
        } else {
            navigate(`/gor/${gor.id}`, { state: { gor } });
        }
    };

    const handleClosePoster = () => {
        // Simpan index poster yang sudah ditutup
        const closedPosters = sessionStorage.getItem('raketersPosterClosed');
        const closedIndices = closedPosters ? JSON.parse(closedPosters) : [];
        
        if (!closedIndices.includes(currentPosterIndex)) {
            closedIndices.push(currentPosterIndex);
            sessionStorage.setItem('raketersPosterClosed', JSON.stringify(closedIndices));
        }
        
        // Cari poster berikutnya yang belum ditutup
        const nextPosterIndex = posters.findIndex((_, index) => !closedIndices.includes(index));
        
        if (nextPosterIndex !== -1) {
            // Ada poster berikutnya, tampilkan
            setCurrentPosterIndex(nextPosterIndex);
            setOpenPoster(true);
        } else {
            // Semua poster sudah ditutup
            setOpenPoster(false);
        }
    };

    // Track active section for sticky headers
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Beri placeholder untuk future active-section behavior
                        // (tidak diperlukan untuk build saat ini)
                    }
                });
            },
            { threshold: 0.1 }
        );

        if (rankedRef.current) observer.observe(rankedRef.current);
        if (funRef.current) observer.observe(funRef.current);
        if (coachingRef.current) observer.observe(coachingRef.current);

        return () => observer.disconnect();
    }, []);

    const sortGorList = (list: Gor[]) => {
        const getDate = (gor: Gor): Date | null => {
            if (!gor.tanggalTersedia || gor.tanggalTersedia.length === 0) return null;
            const t = gor.tanggalTersedia[0];
            if (!t || t === 'Coming Soon') return null;
            const [d, bulan, y] = t.split(' ');
            const bulanMap: { [key: string]: number } = {
                'Januari': 0, 'Februari': 1, 'Maret': 2, 'April': 3, 'Mei': 4, 'Juni': 5,
                'Juli': 6, 'Agustus': 7, 'September': 8, 'Oktober': 9, 'November': 10, 'Desember': 11
            };
            const monthIdx = bulanMap[bulan] ?? 0;
            const day = parseInt(d);
            const year = parseInt(y);
            if (isNaN(day) || isNaN(year)) return null;
            return new Date(year, monthIdx, day);
        };
        return [...list].sort((a, b) => {
            if (sortBy === 'waktu') {
                const dateA = getDate(a);
                const dateB = getDate(b);
                if (dateA && dateB) return dateA.getTime() - dateB.getTime();
                if (dateA && !dateB) return -1;
                if (!dateA && dateB) return 1;
                return 0;
            } else if (sortBy === 'nama') {
                return a.nama.localeCompare(b.nama);
            } else if (sortBy === 'biaya') {
                const biayaA = a.biaya && a.biaya !== '-' ? parseInt(a.biaya) : Infinity;
                const biayaB = b.biaya && b.biaya !== '-' ? parseInt(b.biaya) : Infinity;
                return biayaA - biayaB;
            } else if (sortBy === 'kapasitas') {
                return (b.kapasitasMaksimal || 0) - (a.kapasitasMaksimal || 0);
            } else if (sortBy === 'pesertaTerbanyak') {
                return (b.jumlahBooking || 0) - (a.jumlahBooking || 0);
            } else if (sortBy === 'pesertaTersedikit') {
                return (a.jumlahBooking || 0) - (b.jumlahBooking || 0);
            }
            return 0;
        });
    };

    const formatBiaya = (biaya?: string | null): string => {
        if (!biaya || biaya === '-' || biaya.trim() === '') return 'Rp -';
        const clean = biaya.toString().replace(/[^\d]/g, '');
        if (!clean) return 'Rp -';
        return `Rp${parseInt(clean, 10).toLocaleString('id-ID')}`;
    };

    const getGorPricePreview = (gor: Gor) => {
        if (!gor.tanggalTersedia || gor.tanggalTersedia.length === 0 || gor.tanggalTersedia[0] === 'Coming Soon') {
            return null;
        }

        const normalPrice = gor.biaya && gor.biaya !== '-' ? Number(gor.biaya.toString().replace(/[^\d]/g, '') || '0') : 0;
        const capacity = Number(gor.kapasitasMaksimal || 0);
        const currentParticipants = Number(gor.jumlahPemain ?? gor.jumlahBooking ?? 0);
        const selectedDate = gor.tanggalTersedia[0];
        const timeValue = (gor.jam || '19.00').split('-')[0].trim();
        const [hourStr, minuteStr] = timeValue.includes(':') ? timeValue.split(':') : timeValue.split('.');
        const [day, monthName, year] = selectedDate.split(' ');
        const monthMap: { [key: string]: number } = {
            'Januari': 0, 'Februari': 1, 'Maret': 2, 'April': 3, 'Mei': 4, 'Juni': 5,
            'Juli': 6, 'Agustus': 7, 'September': 8, 'Oktober': 9, 'November': 10, 'Desember': 11
        };

        const monthIndex = monthMap[monthName as keyof typeof monthMap];
        if (!monthIndex && monthIndex !== 0) return null;

        const scheduleStartAt = new Date(Number(year), monthIndex, Number(day), Number(hourStr || 19), Number(minuteStr || 0), 0);

        if (!normalPrice || !capacity || !scheduleStartAt || Number.isNaN(scheduleStartAt.getTime())) {
            return null;
        }

        return calculateBookingPrice({
            normalPrice,
            capacity,
            currentParticipants,
            bookingCount: Number(gor.jumlahBookingJadwal ?? currentParticipants),
            accumulatedDiscountAmount: Number(gor.akumulasiDiskon || 0),
            scheduleStartAt,
            bookingAt: new Date(),
        });
    };

    const normalizeTipe = (tipe?: string | null): string => {
        if (!tipe || tipe.trim() === '') return 'Coaching';
        const normalized = tipe.trim().toLowerCase();
        if (normalized.includes('ranked')) return 'Ranked Match';
        if (normalized.includes('fun')) return 'Fun Match';
        if (normalized.includes('coaching')) return 'Coaching';
        return tipe
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Component for sticky section header
    const StickySection = React.forwardRef<HTMLDivElement, { title: string; sectionIndex: number; children: React.ReactNode }>(
        ({ title, sectionIndex, children }, ref) => {
            const headerHeight = isMobile ? 56 : 64;
            const topOffset = headerHeight;
            const zIndex = 200 + sectionIndex;

            return (
                <Box sx={{ mt: isMobile ? 1 : 2, px: isMobile ? 2 : 6 }}>
                    <Box
                        ref={ref}
                        sx={{
                            position: 'sticky',
                            top: topOffset,
                            zIndex: zIndex,
                            backgroundImage: isDark
                                ? 'linear-gradient(180deg, rgba(20, 25, 50, 0.98) 0%, rgba(20, 25, 50, 0.92) 100%)'
                                : 'linear-gradient(180deg, rgba(255, 152, 0, 0.95) 0%, rgba(255, 193, 7, 0.95) 100%)',
                            backdropFilter: 'blur(12px)',
                            borderBottom: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.1)',
                            padding: isMobile ? '16px 14px' : '14px 14px',
                            mb: isMobile ? 0.5 : 1,
                            scrollBehavior: 'smooth',
                            transition: 'all 0.2s ease-in-out',
                            boxShadow: isDark 
                                ? '0 4px 12px rgba(0,0,0,0.3)' 
                                : '0 2px 8px rgba(0,0,0,0.08)',
                        }}
                    >
                        <Typography 
                            variant={isMobile ? "h6" : "h5"} 
                            sx={{ 
                                fontWeight: 'bold',
                                fontSize: isMobile ? '0.95rem' : undefined,
                                color: '#fff',
                                margin: 0,
                            }}
                        >
                            {title}
                        </Typography>
                    </Box>
                    <Box sx={{ pt: isMobile ? 0.5 : 1 }}>
                        {children}
                    </Box>
                </Box>
            );
        }
    );

    const renderGorCard = (gor: Gor) => {
        const pricingPreview = getGorPricePreview(gor);
        const hasDiscount = normalizeTipe(gor.tipe) !== 'Coaching'
            && Boolean(pricingPreview && pricingPreview.finalDiscountPercentage > 0);
        const gorType = normalizeTipe(gor.tipe);
        const typeColor = gorType === 'Ranked Match'
            ? '#dc6542'
            : gorType === 'Fun Match'
                ? '#d64c77'
                : gorType === 'Coaching'
                    ? '#556fba'
                    : '#253547';
        const scheduleDate = gor.tanggalTersedia?.[0];
        const hasSchedule = Boolean(scheduleDate && scheduleDate !== 'Coming Soon');
        const participantCount = Number(gor.jumlahPemain ?? gor.jumlahBooking ?? 0);
        const capacity = Number(gor.kapasitasMaksimal || 0);
        const occupancy = capacity > 0 ? Math.min((participantCount / capacity) * 100, 100) : 0;

        return (
        <Grid item key={gor.id} xs={12} sm={6} md={4}>
            <Card
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: gor.bisaBooking ? 'pointer' : 'not-allowed',
                    opacity: gor.bisaBooking ? 1 : 0.7,
                    background: isDark ? '#101c29' : '#ffffff',
                    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(16,35,52,0.1)',
                    boxShadow: isDark ? '0 10px 28px rgba(0,0,0,0.22)' : '0 8px 24px rgba(22,45,58,0.1)',
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                    '&:hover': {
                        transform: gor.bisaBooking ? 'translateY(-3px)' : 'none',
                        boxShadow: isDark ? '0 16px 34px rgba(0,0,0,0.32)' : '0 14px 30px rgba(22,45,58,0.16)',
                        borderColor: isDark ? 'rgba(132,205,170,0.42)' : 'rgba(38,132,99,0.3)',
                    }
                }}
                onClick={() => gor.bisaBooking && handleGorClick(gor)}
            >
                <Box sx={{ position: 'relative', overflow: 'hidden', aspectRatio: isMobile ? '1.7 / 1' : '16 / 9' }}>
                    <CardMedia
                        component="img"
                        image={gor.gambar}
                        alt={gor.nama}
                        sx={{
                            display: 'block',
                            height: '100%',
                            objectFit: 'cover',
                            width: '100%',
                            transition: 'transform 350ms ease',
                            'article:hover &': { transform: 'scale(1.035)' },
                        }}
                    />
                    <Box sx={{
                        position: 'absolute',
                        inset: '35% 0 0',
                        background: 'linear-gradient(180deg, rgba(5,14,18,0) 0%, rgba(5,14,18,0.58) 100%)',
                        pointerEvents: 'none',
                    }} />
                    <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 0.75, alignItems: 'center' }}>
                        <Box sx={{
                            px: 1.1,
                            py: 0.55,
                            color: '#fff',
                            bgcolor: typeColor,
                            borderRadius: 0.75,
                            fontSize: isMobile ? '0.62rem' : '0.7rem',
                            fontWeight: 800,
                            lineHeight: 1,
                            letterSpacing: '0.04em',
                            boxShadow: '0 3px 12px rgba(0,0,0,0.22)',
                        }}>
                            {gorType.toUpperCase()}
                        </Box>
                        {!gor.bisaBooking && (
                            <Box sx={{ px: 0.9, py: 0.55, bgcolor: 'rgba(18,28,34,0.78)', color: '#fff', borderRadius: 0.75, fontSize: '0.65rem', fontWeight: 700 }}>
                                SEGERA
                            </Box>
                        )}
                    </Box>
                    {hasDiscount && pricingPreview && (
                        <Box sx={{ position: 'absolute', top: 12, right: 12, px: 1, py: 0.65, borderRadius: 0.75, bgcolor: '#f4b942', color: '#172b2a', fontSize: isMobile ? '0.68rem' : '0.74rem', fontWeight: 900, lineHeight: 1, boxShadow: '0 3px 12px rgba(0,0,0,0.22)' }}>
                            -{pricingPreview.finalDiscountPercentage}%
                        </Box>
                    )}
                    <Box sx={{ position: 'absolute', left: 14, bottom: 12, display: 'flex', alignItems: 'center', gap: 0.65, color: '#fff', fontWeight: 700, fontSize: isMobile ? '0.72rem' : '0.8rem', textShadow: '0 1px 6px rgba(0,0,0,0.7)' }}>
                        <AccessTime sx={{ fontSize: isMobile ? 16 : 18 }} />
                        {gor.jam && gor.jam !== '-' ? gor.jam : 'Jadwal belum tersedia'}
                    </Box>
                </Box>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 1.25, p: isMobile ? 1.6 : 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                        <Typography sx={{ color: isDark ? '#f3f6f5' : '#172a34', fontSize: isMobile ? '1rem' : '1.08rem', fontWeight: 850, lineHeight: 1.2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {gor.nama}
                        </Typography>
                        <Box sx={{ flexShrink: 0, textAlign: 'right', mt: -0.15 }}>
                            {hasDiscount && (
                                <Typography sx={{ color: isDark ? '#9eaaa9' : '#75817f', fontSize: isMobile ? '0.68rem' : '0.72rem', fontWeight: 600, lineHeight: 1.05, textDecoration: 'line-through', textDecorationThickness: '1.5px' }}>
                                    {formatBiaya(gor.biaya)}
                                </Typography>
                            )}
                            <Typography sx={{ color: hasDiscount ? '#27805c' : (isDark ? '#f4f7f7' : '#172a34'), fontSize: isMobile ? '1rem' : '1.08rem', fontWeight: 900, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                                {hasDiscount && pricingPreview ? formatBiaya(String(pricingPreview.finalPrice)) : formatBiaya(gor.biaya)}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.65, minWidth: 0, color: isDark ? '#aab8be' : '#637277' }}>
                        <Place sx={{ flexShrink: 0, fontSize: 17, mt: 0.05 }} />
                        <Typography component="div" sx={{ minWidth: 0, fontSize: isMobile ? '0.75rem' : '0.8rem', lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
                            {renderAddressWithMapLink(gor.alamat)}
                        </Typography>
                    </Box>
                    <Box sx={{ mt: 'auto', pt: 1.1, borderTop: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(23,42,52,0.1)' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0, color: hasSchedule ? (isDark ? '#b8dbca' : '#267653') : '#c65b4b' }}>
                                <CalendarMonth sx={{ flexShrink: 0, fontSize: 17 }} />
                                <Typography sx={{ fontSize: isMobile ? '0.72rem' : '0.78rem', fontWeight: 750, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {hasSchedule ? `${getHariFromTanggal(scheduleDate)}, ${scheduleDate}` : 'Coming Soon'}
                                </Typography>
                            </Box>
                            <Typography sx={{ flexShrink: 0, color: isDark ? '#c4d1d2' : '#536269', fontSize: isMobile ? '0.7rem' : '0.76rem', fontWeight: 800 }}>
                                {participantCount}/{capacity} pemain
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={occupancy}
                            sx={{
                                height: 5,
                                borderRadius: 5,
                                bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(23,42,52,0.09)',
                                '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: occupancy >= 85 ? '#d8873d' : '#46a879' },
                            }}
                        />
                        {(gor.jumlahWaitinglist ?? 0) > 0 && (
                            <Typography sx={{ mt: 0.7, color: isDark ? '#b8dbca' : '#267653', fontSize: '0.7rem', fontWeight: 700 }}>
                                +{gor.jumlahWaitinglist} reservasi
                            </Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Grid>
    );
};

    if (loading) {
        return (
            <Container>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>RaketerS - The Next Level Mabar | Mabar Badminton & Bulutangkis Bandung</title>
                <meta name="description" content="RaketerS adalah platform mabar dan komunitas badminton & bulutangkis Bandung. Booking mabar, temukan teman main, dan ikuti ranked match. The Next Level Mabar! Cari dan main bareng badminton/bulutangkis di Bandung, mudah dan seru. #MabarBadminton #MabarBulutangkis #MabarBandung #TheNextLevelMabar" />
                <meta name="keywords" content="the next level mabar, raketers, mabar badminton, mabar badminton bandung, main bareng badminton bandung, mabar bulutangkis, mabar bulutangkis bandung, main bareng bulutangkis bandung, mabar bandung, main bareng bandung, badminton bandung, bulutangkis bandung, komunitas badminton bandung, komunitas bulutangkis bandung, booking lapangan badminton bandung, booking lapangan bulutangkis bandung, teman main badminton bandung, teman main bulutangkis bandung, ranked match, komunitas mabar, raketers bandung, bulutangkis, badminton, komunitas badminton, komunitas bulutangkis, bulutangkis jawa barat, badminton jawa barat" />
                <link rel="canonical" href="https://raketers.com/" />
                <meta property="og:title" content="RaketerS - The Next Level Mabar | Mabar Badminton & Bulutangkis Bandung" />
                <meta property="og:description" content="Cari teman dan main bareng badminton/bulutangkis di Bandung dengan mudah. Bergabunglah di komunitas RaketerS: The Next Level Mabar!" />
                <meta property="og:url" content="https://raketers.com/" />
                <meta property="og:type" content="website" />
                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:title" content="RaketerS - The Next Level Mabar | Mabar Badminton & Bulutangkis Bandung" />
                <meta property="twitter:description" content="Temukan & booking mabar badminton atau mabar bulutangkis di Bandung. The Next Level Mabar bersama RaketerS." />
            </Helmet>
            {/* Popup Poster */}
            {posters.length > 0 && posters[currentPosterIndex] && (
                <Dialog open={openPoster} onClose={handleClosePoster} maxWidth="md">
                    <DialogContent sx={{ position: 'relative', p: 0, bgcolor: '#000' }}>
                        <IconButton aria-label="close" onClick={handleClosePoster} sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: 'white',
                            zIndex: 2,
                            background: 'rgba(0,0,0,0.4)',
                            backdropFilter: 'blur(3px)',
                            '&:hover': {
                                background: 'rgba(0,0,0,0.7)',
                            },
                        }}>
                            <CloseIcon />
                        </IconButton>
                        <img
                            src={posters[currentPosterIndex].imageUrl}
                            alt="Poster RaketerS"
                            style={{
                                width: '100%',
                                maxWidth: '90vw',
                                maxHeight: '85vh',
                                objectFit: 'contain',
                                display: 'block',
                                margin: '0 auto',
                                borderRadius: '16px',
                                cursor: posters[currentPosterIndex].link ? 'pointer' : 'default'
                            }}
                            onClick={() => {
                                const currentPoster = posters[currentPosterIndex];
                                
                                // Simpan index poster yang sudah ditutup
                                const closedPosters = sessionStorage.getItem('raketersPosterClosed');
                                const closedIndices = closedPosters ? JSON.parse(closedPosters) : [];
                                
                                if (!closedIndices.includes(currentPosterIndex)) {
                                    closedIndices.push(currentPosterIndex);
                                    sessionStorage.setItem('raketersPosterClosed', JSON.stringify(closedIndices));
                                }
                                
                                // Jika ada link, redirect ke link tersebut
                                if (currentPoster.link) {
                                    window.location.href = currentPoster.link;
                                    return;
                                }
                                
                                // Jika tidak ada link, cari poster berikutnya yang belum ditutup
                                const nextPosterIndex = posters.findIndex((_, index) => !closedIndices.includes(index));
                                
                                if (nextPosterIndex !== -1) {
                                    // Ada poster berikutnya, tampilkan
                                    setCurrentPosterIndex(nextPosterIndex);
                                    setOpenPoster(true);
                                } else {
                                    // Semua poster sudah ditutup
                                    setOpenPoster(false);
                                }
                            }}
                        />
                    </DialogContent>
                </Dialog>
            )}
            {/* --- */}
            <Container
                maxWidth={isMobile ? false : 'lg'}
                disableGutters={isMobile}
                sx={{ px: isMobile ? 0 : 3 }}
            >
                <Paper 
                    elevation={0}
                    sx={{
                        background: isDark ? '#0a1626' : 'linear-gradient(135deg, rgba(227,242,253,0.9) 0%, rgba(187,222,251,0.7) 100%)',
                        ...(isDark ? {
                            backgroundImage: `radial-gradient(circle at 16% 88%, rgba(255,255,255,0.17) 1px, transparent 1.7px),
                             radial-gradient(circle at 80% 77%, rgba(255,255,255,0.10) 1.2px, transparent 1.7px),
                             radial-gradient(circle at 70% 92%, rgba(255,255,255,0.11) 1px, transparent 1.5px)`,
                            color: '#fafcff'
                        } : {}),
                        border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1.5px solid rgba(255,255,255,0.2)',
                        boxShadow: isDark ? '0 8px 32px rgba(10, 22, 38, 0.6)' : '0 8px 32px rgba(25,118,210,0.1)',
                        py: isMobile ? 2.5 : 3.5,
                        px: isMobile ? 2 : 5,
                        mt: isMobile ? 2 : 4,
                        mb: isMobile ? 3 : 6,
                        borderRadius: 5,
                        textAlign: "center"
                    }}
                >
                    <Link to="/" style={{ display: 'inline-block' }}>
                        <Box
                            component="img"
                            src="/images/logo_booking_mabar.png"
                            alt="RaketerS Logo"
                            sx={{
                                height: isMobile ? 80 : 120,
                                mb: isMobile ? 0 : 0.5,
                                width: 'auto',
                                borderRadius: 4,
                                boxShadow: isDark 
                                    ? '0 8px 32px rgba(13,71,161,0.4)'
                                    : '0 8px 32px rgba(25,118,210,0.15)',
                                transition: 'all 0.3s ease',
                                animation: 'float 3s ease-in-out infinite',
                                '&:hover': {
                                    transform: 'scale(1.1) rotate(5deg)',
                                    boxShadow: isDark 
                                        ? '0 12px 40px rgba(13,71,161,0.5)'
                                        : '0 12px 40px rgba(25,118,210,0.25)',
                                    animation: 'pulse 0.6s ease-in-out',
                                },
                                '@keyframes float': {
                                    '0%, 100%': {
                                        transform: 'translateY(0px)',
                                    },
                                    '50%': {
                                        transform: 'translateY(-10px)',
                                    },
                                },
                                '@keyframes pulse': {
                                    '0%': {
                                        transform: 'scale(1.1) rotate(5deg)',
                                    },
                                    '50%': {
                                        transform: 'scale(1.15) rotate(5deg)',
                                    },
                                    '100%': {
                                        transform: 'scale(1.1) rotate(5deg)',
                                    },
                                },
                            }}
                        />
                    </Link>
                    <Typography 
                        variant={isMobile ? "h5" : "h3"}
                        color="primary"
                        sx={{ fontWeight: 700, mt: isMobile ? 1 : 2 }}
                    >
                        RaketerS - The Next Level Mabar
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2, mt: 1, fontSize: isMobile ? '0.95rem' : '1.15rem' }}>
                        Platform Mabar Badminton & Bulutangkis Bandung Terbaik — Temukan partner dan booking mabar badminton favoritmu di Bandung dengan mudah. Ikut ranked match, main bareng komunitas badminton Bandung terbesar. Mabar sekarang makin gampang!
                    </Typography>
                    <Divider sx={{ 
                        mt: isMobile ? 1 : 1.5, 
                        mb: isMobile ? 3 : 5, 
                        borderColor: 'transparent', 
                        opacity: 1, 
                        borderBottomWidth: 0,
                        background: isDark 
                            ? 'linear-gradient(90deg, transparent 0%, rgba(99,170,255,0.75) 50%, transparent 100%)'
                            : 'linear-gradient(90deg, transparent 0%, #42a5f5 50%, transparent 100%)',
                        height: isMobile ? 2 : 3,
                        borderRadius: 2,
                        boxShadow: isDark ? '0 0 10px rgba(99,170,255,0.25)' : 'none'
                    }} />
                    {/* Banner Slider */}
                    {banners.length > 0 && (
                        <Box
                            sx={{
                                mt: 0,
                                mb: isMobile ? 2 : 3,
                                mx: 'auto',
                                width: '100%',
                                maxWidth: '100%',
                                borderRadius: 3,
                                boxShadow: isDark 
                                    ? '0 8px 32px rgba(13,71,161,0.4)'
                                    : '0 8px 32px rgba(25,118,210,0.15)',
                                overflow: 'hidden',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                border: isDark 
                                    ? '1px solid rgba(255,255,255,0.1)'
                                    : '1px solid rgba(255,255,255,0.2)',
                                '&:hover': {
                                    transform: 'scale(1.02)',
                                    boxShadow: isDark 
                                        ? '0 12px 40px rgba(13,71,161,0.5)'
                                        : '0 12px 40px rgba(25,118,210,0.2)',
                                },
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: isDark 
                                        ? 'linear-gradient(45deg, rgba(13,71,161,0.3) 0%, rgba(21,101,192,0.1) 100%)'
                                        : 'linear-gradient(45deg, rgba(227,242,253,0.3) 0%, rgba(187,222,251,0.1) 100%)',
                                    zIndex: 1,
                                    pointerEvents: 'none',
                                }
                            }}
                            onClick={(e) => {
                                // Hanya navigate jika yang diklik bukan tombol navigasi atau dots
                                const target = e.target as HTMLElement;
                                if (target.closest && target.closest('[data-banner-control]')) {
                                    return;
                                }
                                // Jika banner punya link, redirect ke link
                                const banner = banners[currentBannerIndex];
                                if (banner.link) {
                                    window.open(banner.link, '_blank');
                                    return;
                                }
                                navigate('/coaching-clinic');
                            }}
                        >
                            {/* Banner Images */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    width: `${banners.length * 100}%`,
                                    height: 'auto',
                                    transform: `translateX(-${currentBannerIndex * (100 / banners.length)}%)`,
                                    transition: 'transform 0.5s ease-in-out'
                                }}
                            >
                                {banners.map((banner, index) => (
                                    banner.link ? (
                                        <a
                                            key={index}
                                            href={banner.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ width: `${100 / banners.length}%`, display: 'block' }}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <Box
                                                component="img"
                                                src={banner.imageUrl}
                                                alt={`Banner ${index + 1}`}
                                                sx={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    objectFit: 'cover',
                                                    display: 'block'
                                                }}
                                            />
                                        </a>
                                    ) : (
                                        <Box
                                            key={index}
                                            component="img"
                                            src={banner.imageUrl}
                                            alt={`Banner ${index + 1}`}
                                            sx={{
                                                width: `${100 / banners.length}%`,
                                                height: 'auto',
                                                objectFit: 'cover',
                                                display: 'block'
                                            }}
                                        />
                                    )
                                ))}
                            </Box>

                            {/* Navigation Arrows */}
                            {banners.length > 1 && (
                                <>
                                    <IconButton
                                        data-banner-control="prev"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleBannerPrevious();
                                        }}
                                        onMouseUp={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onTouchStart={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onTouchEnd={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            left: 16,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                                            backdropFilter: 'blur(10px)',
                                            color: theme.palette.primary.main,
                                            border: '1px solid rgba(255,255,255,0.3)',
                                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                                            transition: 'all 0.3s ease',
                                            zIndex: 10,
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.8) 100%)',
                                                transform: 'translateY(-50%) scale(1.1)',
                                                boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                                            },
                                            display: isMobile ? 'none' : 'flex'
                                        }}
                                    >
                                        <ChevronLeft />
                                    </IconButton>

                                    <IconButton
                                        data-banner-control="next"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleBannerNext();
                                        }}
                                        onMouseUp={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onTouchStart={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onTouchEnd={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            right: 16,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                                            backdropFilter: 'blur(10px)',
                                            color: theme.palette.primary.main,
                                            border: '1px solid rgba(255,255,255,0.3)',
                                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                                            transition: 'all 0.3s ease',
                                            zIndex: 10,
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.8) 100%)',
                                                transform: 'translateY(-50%) scale(1.1)',
                                                boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                                            },
                                            display: isMobile ? 'none' : 'flex'
                                        }}
                                    >
                                        <ChevronRight />
                                    </IconButton>
                                </>
                            )}

                            {/* Dots Indicator */}
                            {banners.length > 1 && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 16,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        display: 'flex',
                                        gap: 1,
                                        zIndex: 10
                                    }}
                                >
                                    {banners.map((_, index) => (
                                        <Box
                                            key={index}
                                            data-banner-control="dot"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleBannerDotClick(index);
                                            }}
                                            onMouseUp={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                background: index === currentBannerIndex 
                                                    ? 'linear-gradient(45deg, #1976d2, #42a5f5)'
                                                    : 'rgba(255, 255, 255, 0.5)',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                boxShadow: index === currentBannerIndex 
                                                    ? '0 2px 8px rgba(25, 118, 210, 0.4)'
                                                    : 'none',
                                                '&:hover': {
                                                    transform: 'scale(1.2)',
                                                    background: index === currentBannerIndex 
                                                        ? 'linear-gradient(45deg, #1565c0, #1976d2)'
                                                        : 'rgba(255, 255, 255, 0.8)',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    <Box sx={{
                        display: isMobile ? 'block' : 'flex',
                        justifyContent: isMobile ? 'unset' : 'flex-end',
                        alignItems: isMobile ? 'unset' : 'center',
                        gap: isMobile ? 0 : 2,
                        mt: isMobile ? 3 : 4,
                        mb: isMobile ? 1 : 0
                    }}>
                        <Box sx={{
                            mb: isMobile ? 2 : 0,
                            flex: isMobile ? 'unset' : '1 1 auto',
                            minWidth: isMobile ? '100%' : 220,
                            maxWidth: isMobile ? '100%' : 320
                        }}>
                            <input
                                type="text"
                                placeholder="Cari GOR..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    borderRadius: 12,
                                    border: isDark ? '1.5px solid rgba(255,255,255,0.2)' : '1.5px solid rgba(255,255,255,0.3)',
                                    fontSize: 16,
                                    outline: 'none',
                                    background: isDark 
                                        ? 'linear-gradient(135deg, rgba(13,71,161,0.1) 0%, rgba(21,101,192,0.05) 100%)'
                                        : 'linear-gradient(135deg, rgba(227,242,253,0.95) 0%, rgba(187,222,251,0.8) 100%)',
                                    backdropFilter: 'blur(10px)',
                                    color: isDark ? 'white' : '#222',
                                    boxShadow: isDark 
                                        ? '0 8px 32px rgba(13,71,161,0.3)'
                                        : '0 8px 32px rgba(25,118,210,0.1)',
                                    transition: 'all 0.3s ease',
                                }}
                                onFocus={(e) => {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = isDark 
                                        ? '0 12px 40px rgba(13,71,161,0.4)'
                                        : '0 12px 40px rgba(25,118,210,0.15)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = isDark 
                                        ? '0 8px 32px rgba(13,71,161,0.3)'
                                        : '0 8px 32px rgba(25,118,210,0.1)';
                                }}
                            />
                            <style>{`
                              input[placeholder="Cari GOR..."]::placeholder {
                                color: ${isDark ? 'white' : 'gray'} !important;
                                opacity: 1 !important;
                              }
                            `}</style>
                        </Box>
                        <Paper elevation={2} sx={{
                            display: 'inline-block',
                            borderRadius: 3,
                            border: '1.5px solid',
                            borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.3)',
                            boxShadow: isDark 
                                ? '0 8px 32px rgba(13,71,161,0.3)'
                                : '0 8px 32px rgba(25,118,210,0.1)',
                            background: isDark 
                                ? 'linear-gradient(135deg, rgba(13,71,161,0.1) 0%, rgba(21,101,192,0.05) 100%)'
                                : 'linear-gradient(135deg, rgba(227,242,253,0.95) 0%, rgba(187,222,251,0.8) 100%)',
                            backdropFilter: 'blur(10px)',
                            px: 1.5,
                            py: 1.2,
                            textAlign: 'right',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: isDark 
                                    ? '0 12px 40px rgba(13,71,161,0.4)'
                                    : '0 12px 40px rgba(25,118,210,0.15)',
                                transform: 'translateY(-2px)',
                            }
                        }}>
                            <FormControl size="small" variant="outlined" sx={{ minWidth: 170, width: 240, background: 'transparent', mt: 0 }}>
                                <InputLabel 
                                    id="sort-gor-label" 
                                    sx={{ 
                                        fontWeight: 600, 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        color: isDark ? 'white' : 'primary.main',
                                        transition: 'all 0.3s ease',
                                        animation: 'fadeIn 2s ease-in-out infinite alternate',
                                        '@keyframes fadeIn': {
                                            '0%': {
                                                opacity: 0.8,
                                            },
                                            '100%': {
                                                opacity: 1,
                                            },
                                        },
                                        '&:hover': {
                                            transform: 'scale(1.02)',
                                            textShadow: isDark 
                                                ? '0 2px 8px rgba(255,255,255,0.3)'
                                                : '0 2px 8px rgba(25, 118, 210, 0.3)',
                                            animation: 'pulse 0.6s ease-in-out infinite',
                                            '@keyframes pulse': {
                                                '0%': {
                                                    transform: 'scale(1.02)',
                                                },
                                                '50%': {
                                                    transform: 'scale(1.05)',
                                                },
                                                '100%': {
                                                    transform: 'scale(1.02)',
                                                },
                                            },
                                        }
                                    }} 
                                />
                                <Select
                                    labelId="sort-gor-label"
                                    value={sortBy}
                                    label=""
                                    onChange={(e) => setSortBy(e.target.value as any)}
                                    sx={{ 
                                        color: isDark ? 'white' : 'primary.main', 
                                        fontWeight: 600,
                                        transition: 'all 0.3s ease',
                                        animation: 'fadeIn 2s ease-in-out infinite alternate',
                                        '@keyframes fadeIn': {
                                            '0%': {
                                                opacity: 0.8,
                                            },
                                            '100%': {
                                                opacity: 1,
                                            },
                                        },
                                        '&:hover': {
                                            transform: 'scale(1.02)',
                                            animation: 'pulse 0.6s ease-in-out infinite',
                                            '@keyframes pulse': {
                                                '0%': {
                                                    transform: 'scale(1.02)',
                                                },
                                                '50%': {
                                                    transform: 'scale(1.05)',
                                                },
                                                '100%': {
                                                    transform: 'scale(1.02)',
                                                },
                                            },
                                        }
                                    }}
                                >
                                    <MenuItem 
                                        value="waktu"
                                        sx={{
                                            transition: 'all 0.3s ease',
                                            animation: 'fadeIn 2s ease-in-out infinite alternate',
                                            '@keyframes fadeIn': {
                                                '0%': {
                                                    opacity: 0.8,
                                                },
                                                '100%': {
                                                    opacity: 1,
                                                },
                                            },
                                            '&:hover': {
                                                transform: 'scale(1.02)',
                                                backgroundColor: isDark 
                                                    ? 'rgba(255,255,255,0.1)'
                                                    : 'rgba(25, 118, 210, 0.1)',
                                                animation: 'pulse 0.6s ease-in-out infinite',
                                                '@keyframes pulse': {
                                                    '0%': {
                                                        transform: 'scale(1.02)',
                                                    },
                                                    '50%': {
                                                        transform: 'scale(1.05)',
                                                    },
                                                    '100%': {
                                                        transform: 'scale(1.02)',
                                                    },
                                                },
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SortIcon 
                                                fontSize="small" 
                                                sx={{ 
                                                    color: isDark ? 'white' : 'primary.main',
                                                    transition: 'all 0.3s ease',
                                                    animation: 'fadeIn 2s ease-in-out infinite alternate',
                                                    '@keyframes fadeIn': {
                                                        '0%': {
                                                            opacity: 0.8,
                                                        },
                                                        '100%': {
                                                            opacity: 1,
                                                        },
                                                    },
                                                    '&:hover': {
                                                        transform: 'rotate(180deg) scale(1.1)',
                                                        animation: 'pulse 0.6s ease-in-out infinite',
                                                        '@keyframes pulse': {
                                                            '0%': {
                                                                transform: 'rotate(180deg) scale(1.1)',
                                                            },
                                                            '50%': {
                                                                transform: 'rotate(180deg) scale(1.2)',
                                                            },
                                                            '100%': {
                                                                transform: 'rotate(180deg) scale(1.1)',
                                                            },
                                                        },
                                                    }
                                                }} 
                                            />
                                            Waktu Terdekat
                                        </Box>
                                    </MenuItem>
                                    <MenuItem 
                                        value="nama"
                                        sx={{
                                            transition: 'all 0.3s ease',
                                            animation: 'fadeIn 2s ease-in-out infinite alternate',
                                            '@keyframes fadeIn': {
                                                '0%': {
                                                    opacity: 0.8,
                                                },
                                                '100%': {
                                                    opacity: 1,
                                                },
                                            },
                                            '&:hover': {
                                                transform: 'scale(1.02)',
                                                backgroundColor: isDark 
                                                    ? 'rgba(255,255,255,0.1)'
                                                    : 'rgba(25, 118, 210, 0.1)',
                                                animation: 'pulse 0.6s ease-in-out infinite',
                                                '@keyframes pulse': {
                                                    '0%': {
                                                        transform: 'scale(1.02)',
                                                    },
                                                    '50%': {
                                                        transform: 'scale(1.05)',
                                                    },
                                                    '100%': {
                                                        transform: 'scale(1.02)',
                                                    },
                                                },
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SortIcon 
                                                fontSize="small" 
                                                sx={{ 
                                                    color: isDark ? 'white' : 'primary.main',
                                                    transition: 'all 0.3s ease',
                                                    animation: 'fadeIn 2s ease-in-out infinite alternate',
                                                    '@keyframes fadeIn': {
                                                        '0%': {
                                                            opacity: 0.8,
                                                        },
                                                        '100%': {
                                                            opacity: 1,
                                                        },
                                                    },
                                                    '&:hover': {
                                                        transform: 'rotate(180deg) scale(1.1)',
                                                        animation: 'pulse 0.6s ease-in-out infinite',
                                                        '@keyframes pulse': {
                                                            '0%': {
                                                                transform: 'rotate(180deg) scale(1.1)',
                                                            },
                                                            '50%': {
                                                                transform: 'rotate(180deg) scale(1.2)',
                                                            },
                                                            '100%': {
                                                                transform: 'rotate(180deg) scale(1.1)',
                                                            },
                                                        },
                                                    }
                                                }} 
                                            />
                                            Nama (A-Z)
                                        </Box>
                                    </MenuItem>
                                    <MenuItem 
                                        value="biaya"
                                        sx={{
                                            transition: 'all 0.3s ease',
                                            animation: 'fadeIn 2s ease-in-out infinite alternate',
                                            '@keyframes fadeIn': {
                                                '0%': {
                                                    opacity: 0.8,
                                                },
                                                '100%': {
                                                    opacity: 1,
                                                },
                                            },
                                            '&:hover': {
                                                transform: 'scale(1.02)',
                                                backgroundColor: isDark 
                                                    ? 'rgba(255,255,255,0.1)'
                                                    : 'rgba(25, 118, 210, 0.1)',
                                                animation: 'pulse 0.6s ease-in-out infinite',
                                                '@keyframes pulse': {
                                                    '0%': {
                                                        transform: 'scale(1.02)',
                                                    },
                                                    '50%': {
                                                        transform: 'scale(1.05)',
                                                    },
                                                    '100%': {
                                                        transform: 'scale(1.02)',
                                                    },
                                                },
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SortIcon 
                                                fontSize="small" 
                                                sx={{ 
                                                    color: isDark ? 'white' : 'primary.main',
                                                    transition: 'all 0.3s ease',
                                                    animation: 'fadeIn 2s ease-in-out infinite alternate',
                                                    '@keyframes fadeIn': {
                                                        '0%': {
                                                            opacity: 0.8,
                                                        },
                                                        '100%': {
                                                            opacity: 1,
                                                        },
                                                    },
                                                    '&:hover': {
                                                        transform: 'rotate(180deg) scale(1.1)',
                                                        animation: 'pulse 0.6s ease-in-out infinite',
                                                        '@keyframes pulse': {
                                                            '0%': {
                                                                transform: 'rotate(180deg) scale(1.1)',
                                                            },
                                                            '50%': {
                                                                transform: 'rotate(180deg) scale(1.2)',
                                                            },
                                                            '100%': {
                                                                transform: 'rotate(180deg) scale(1.1)',
                                                            },
                                                        },
                                                    }
                                                }} 
                                            />
                                            Biaya Termurah
                                        </Box>
                                    </MenuItem>
                                    <MenuItem 
                                        value="kapasitas"
                                        sx={{
                                            transition: 'all 0.3s ease',
                                            animation: 'fadeIn 2s ease-in-out infinite alternate',
                                            '@keyframes fadeIn': {
                                                '0%': {
                                                    opacity: 0.8,
                                                },
                                                '100%': {
                                                    opacity: 1,
                                                },
                                            },
                                            '&:hover': {
                                                transform: 'scale(1.02)',
                                                backgroundColor: isDark 
                                                    ? 'rgba(255,255,255,0.1)'
                                                    : 'rgba(25, 118, 210, 0.1)',
                                                animation: 'pulse 0.6s ease-in-out infinite',
                                                '@keyframes pulse': {
                                                    '0%': {
                                                        transform: 'scale(1.02)',
                                                    },
                                                    '50%': {
                                                        transform: 'scale(1.05)',
                                                    },
                                                    '100%': {
                                                        transform: 'scale(1.02)',
                                                    },
                                                },
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SortIcon 
                                                fontSize="small" 
                                                sx={{ 
                                                    color: isDark ? 'white' : 'primary.main',
                                                    transition: 'all 0.3s ease',
                                                    animation: 'fadeIn 2s ease-in-out infinite alternate',
                                                    '@keyframes fadeIn': {
                                                        '0%': {
                                                            opacity: 0.8,
                                                        },
                                                        '100%': {
                                                            opacity: 1,
                                                        },
                                                    },
                                                    '&:hover': {
                                                        transform: 'rotate(180deg) scale(1.1)',
                                                        animation: 'pulse 0.6s ease-in-out infinite',
                                                        '@keyframes pulse': {
                                                            '0%': {
                                                                transform: 'rotate(180deg) scale(1.1)',
                                                            },
                                                            '50%': {
                                                                transform: 'rotate(180deg) scale(1.2)',
                                                            },
                                                            '100%': {
                                                                transform: 'rotate(180deg) scale(1.1)',
                                                            },
                                                        },
                                                    }
                                                }} 
                                            />
                                            Pemain Utama Terbesar
                                        </Box>
                                    </MenuItem>
                                    <MenuItem 
                                        value="pesertaTerbanyak"
                                        sx={{
                                            transition: 'all 0.3s ease',
                                            animation: 'fadeIn 2s ease-in-out infinite alternate',
                                            '@keyframes fadeIn': {
                                                '0%': {
                                                    opacity: 0.8,
                                                },
                                                '100%': {
                                                    opacity: 1,
                                                },
                                            },
                                            '&:hover': {
                                                transform: 'scale(1.02)',
                                                backgroundColor: isDark 
                                                    ? 'rgba(255,255,255,0.1)'
                                                    : 'rgba(25, 118, 210, 0.1)',
                                                animation: 'pulse 0.6s ease-in-out infinite',
                                                '@keyframes pulse': {
                                                    '0%': {
                                                        transform: 'scale(1.02)',
                                                    },
                                                    '50%': {
                                                        transform: 'scale(1.05)',
                                                    },
                                                    '100%': {
                                                        transform: 'scale(1.02)',
                                                    },
                                                },
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SortIcon 
                                                fontSize="small" 
                                                sx={{ 
                                                    color: isDark ? 'white' : 'primary.main',
                                                    transition: 'all 0.3s ease',
                                                    animation: 'fadeIn 2s ease-in-out infinite alternate',
                                                    '@keyframes fadeIn': {
                                                        '0%': {
                                                            opacity: 0.8,
                                                        },
                                                        '100%': {
                                                            opacity: 1,
                                                        },
                                                    },
                                                    '&:hover': {
                                                        transform: 'rotate(180deg) scale(1.1)',
                                                        animation: 'pulse 0.6s ease-in-out infinite',
                                                        '@keyframes pulse': {
                                                            '0%': {
                                                                transform: 'rotate(180deg) scale(1.1)',
                                                            },
                                                            '50%': {
                                                                transform: 'rotate(180deg) scale(1.2)',
                                                            },
                                                            '100%': {
                                                                transform: 'rotate(180deg) scale(1.1)',
                                                            },
                                                        },
                                                    }
                                                }} 
                                            />
                                            Peserta Terbanyak
                                        </Box>
                                    </MenuItem>
                                    <MenuItem 
                                        value="pesertaTersedikit"
                                        sx={{
                                            transition: 'all 0.3s ease',
                                            animation: 'fadeIn 2s ease-in-out infinite alternate',
                                            '@keyframes fadeIn': {
                                                '0%': {
                                                    opacity: 0.8,
                                                },
                                                '100%': {
                                                    opacity: 1,
                                                },
                                            },
                                            '&:hover': {
                                                transform: 'scale(1.02)',
                                                backgroundColor: isDark 
                                                    ? 'rgba(255,255,255,0.1)'
                                                    : 'rgba(25, 118, 210, 0.1)',
                                                animation: 'pulse 0.6s ease-in-out infinite',
                                                '@keyframes pulse': {
                                                    '0%': {
                                                        transform: 'scale(1.02)',
                                                    },
                                                    '50%': {
                                                        transform: 'scale(1.05)',
                                                    },
                                                    '100%': {
                                                        transform: 'scale(1.02)',
                                                    },
                                                },
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SortIcon 
                                                fontSize="small" 
                                                sx={{ 
                                                    color: isDark ? 'white' : 'primary.main',
                                                    transition: 'all 0.3s ease',
                                                    animation: 'fadeIn 2s ease-in-out infinite alternate',
                                                    '@keyframes fadeIn': {
                                                        '0%': {
                                                            opacity: 0.8,
                                                        },
                                                        '100%': {
                                                            opacity: 1,
                                                        },
                                                    },
                                                    '&:hover': {
                                                        transform: 'rotate(180deg) scale(1.1)',
                                                        animation: 'pulse 0.6s ease-in-out infinite',
                                                        '@keyframes pulse': {
                                                            '0%': {
                                                                transform: 'rotate(180deg) scale(1.1)',
                                                            },
                                                            '50%': {
                                                                transform: 'rotate(180deg) scale(1.2)',
                                                            },
                                                            '100%': {
                                                                transform: 'rotate(180deg) scale(1.1)',
                                                            },
                                                        },
                                                    }
                                                }} 
                                            />
                                            Peserta Tersedikit
                                        </Box>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Paper>
                    </Box>
                </Paper>
            </Container>

            <Box sx={{ width: '100%', overflow: 'visible', position: 'relative' }}>
                {(() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const getDate = (gor: Gor): Date | null => {
                        if (!gor.tanggalTersedia || gor.tanggalTersedia.length === 0) return null;
                        const t = gor.tanggalTersedia[0];
                        if (!t || t === 'Coming Soon') return null;
                        const [d, bulan, y] = t.split(' ');
                        const bulanMap: { [key: string]: number } = {
                            'Januari': 0, 'Februari': 1, 'Maret': 2, 'April': 3, 'Mei': 4, 'Juni': 5,
                            'Juli': 6, 'Agustus': 7, 'September': 8, 'Oktober': 9, 'November': 10, 'Desember': 11
                        };
                        const monthIdx = bulanMap[bulan] ?? 0;
                        const day = parseInt(d);
                        const year = parseInt(y);
                        if (isNaN(day) || isNaN(year)) return null;
                        return new Date(year, monthIdx, day);
                    };
                    
                    const futureSchedules = [...gorList, ...coachingList]
                        .filter(gor => {
                            const date = getDate(gor);
                            return date && date >= today;
                        })
                        .sort((a, b) => {
                            const dateA = getDate(a);
                            const dateB = getDate(b);
                            if (dateA && dateB) return dateA.getTime() - dateB.getTime();
                            return 0;
                        });
                    
                    if (futureSchedules.length === 0) return null;
                    
                    const nearestDates: Date[] = [];
                    const dateStrings = new Set<string>();
                    for (const gor of futureSchedules) {
                        const date = getDate(gor);
                        if (!date) continue;
                        const dateKey = date.toDateString();
                        if (!dateStrings.has(dateKey)) {
                            dateStrings.add(dateKey);
                            nearestDates.push(date);
                            if (nearestDates.length >= 2) break;
                        }
                    }
                    
                    if (nearestDates.length === 0) return null;
                    
                    const nearestTwoDaysSchedules = futureSchedules.filter(gor => {
                        const date = getDate(gor);
                        return date && nearestDates.some(nearest => nearest.toDateString() === date.toDateString());
                    });
                    
                    return (
                        <StickySection title="JADWAL TERDEKAT" sectionIndex={0}>
                            <Box sx={{ px: isMobile ? 1.5 : 3, py: isMobile ? 1 : 1.5 }}>
                                <Grid container spacing={isMobile ? 0.5 : 2}>
                                    {sortGorList(nearestTwoDaysSchedules).map((gor) => renderGorCard(gor))}
                                </Grid>
                            </Box>
                        </StickySection>
                    );
                })()}
                {(() => {
                    const rankedSchedules = sortGorList(gorList.filter(gor => {
                        const term = searchTerm.toLowerCase();
                        const matchNama = gor.nama.toLowerCase().includes(term);
                        const matchAlamat = gor.alamat.toLowerCase().includes(term);
                        const hariList = gor.tanggalTersedia?.map(t => getHariFromTanggal(t).toLowerCase()) || [];
                        const matchHari = hariList.some(hari => hari.includes(term));
                        const matchTanggal = gor.tanggalTersedia && gor.tanggalTersedia.some(t => t.toLowerCase().includes(term));
                        return (matchNama || matchAlamat || matchHari || matchTanggal) && gor.tipe === 'Ranked Match';
                    }));

                    if (rankedSchedules.length === 0) return null;

                    return (
                        <StickySection ref={rankedRef} title="JADWAL MABAR RANKED MATCH" sectionIndex={1}>
                            <Box sx={{ px: isMobile ? 1.5 : 3, py: isMobile ? 1 : 1.5 }}>
                                <Grid container spacing={isMobile ? 0.5 : 2}>
                                    {rankedSchedules.map((gor) => renderGorCard(gor))}
                                </Grid>
                            </Box>
                        </StickySection>
                    );
                })()}

                <StickySection ref={funRef} title="JADWAL MABAR FUN MATCH" sectionIndex={2}>
                    <Box sx={{ px: isMobile ? 1.5 : 3, py: isMobile ? 1 : 1.5 }}>
                        <Grid container spacing={isMobile ? 0.5 : 2}>
                            {sortGorList(gorList.filter(gor => {
                                const term = searchTerm.toLowerCase();
                                const matchNama = gor.nama.toLowerCase().includes(term);
                                const matchAlamat = gor.alamat.toLowerCase().includes(term);
                                const hariList = gor.tanggalTersedia?.map(t => getHariFromTanggal(t).toLowerCase()) || [];
                                const matchHari = hariList.some(hari => hari.includes(term));
                                const matchTanggal = gor.tanggalTersedia && gor.tanggalTersedia.some(t => t.toLowerCase().includes(term));
                                return (matchNama || matchAlamat || matchHari || matchTanggal) && gor.tipe === 'Fun Match';
                            })).map((gor) => renderGorCard(gor))}
                        </Grid>
                    </Box>
                </StickySection>

                {/* Coaching Section */}
                {coachingList.length > 0 && (
                    <StickySection ref={coachingRef} title="JADWAL COACHING" sectionIndex={3}>
                        <Box sx={{ px: isMobile ? 1.5 : 3, py: isMobile ? 1 : 1.5 }}>
                            <Grid container spacing={isMobile ? 0.5 : 2}>
                                {coachingList.map((coaching) => renderGorCard(coaching))}
                            </Grid>
                        </Box>
                    </StickySection>
                )}
            </Box>
            
            {/* Floating WhatsApp Button */}
            <Tooltip title="Hubungi Kami via WhatsApp" arrow placement="left">
                <Fab
                    color="success"
                    aria-label="whatsapp"
                    sx={{
                        position: 'fixed',
                        bottom: isMobile ? 16 : 24,
                        right: isMobile ? 16 : 24,
                        zIndex: 1000,
                        width: isMobile ? 56 : 64,
                        height: isMobile ? 56 : 64,
                        backgroundColor: '#25D366',
                        '&:hover': {
                            backgroundColor: '#128C7E',
                            transform: 'scale(1.1)',
                            transition: 'transform 0.3s ease-in-out',
                        },
                        boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)',
                    }}
                    onClick={() => window.open('https://wa.me/+6285171513489', '_blank')}
                >
                    <WhatsAppIcon sx={{ fontSize: isMobile ? 32 : 36, color: '#ffffff' }} />
                </Fab>
            </Tooltip>
        </>
    );
};

export default Home; 